/**
 * GroupManagementPage — admin oversight of ALL community groups (ignores
 * ownership). Search + paginated table + force-delete + CSV/PDF export.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Search, Download, FileText, Trash2, ChevronLeft, ChevronRight, X, Eye } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { adminService, type AdminGroupRow, type GroupMember } from '@/services/adminService'
import { exportToCSV, exportToPDF, type ExportColumn } from '@/lib/exportUtils'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'

interface GroupDetail extends AdminGroupRow {}

const GroupViewModal: React.FC<{
  group: GroupDetail | null
  members: GroupMember[]
  loading: boolean
  onClose: () => void
}> = ({ group, members, loading, onClose }) => {
  if (!group) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{group.name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-gray-500">Privacy: </span><span className="font-medium text-gray-900">{group.privacy}</span></div>
            <div><span className="text-gray-500">Category: </span><span className="font-medium text-gray-900">{group.category || '—'}</span></div>
            <div><span className="text-gray-500">Members: </span><span className="font-medium text-gray-900">{group.memberCount}</span></div>
            <div><span className="text-gray-500">Posts: </span><span className="font-medium text-gray-900">{group.postCount}</span></div>
            <div className="col-span-2"><span className="text-gray-500">Created: </span><span className="font-medium text-gray-900">{new Date(group.createdAt).toLocaleString()}</span></div>
          </div>
          {group.description && <p className="text-sm text-gray-700 border-t border-gray-100 pt-3">{group.description}</p>}
          <div className="border-t border-gray-100 pt-3">
            <h4 className="font-medium text-gray-900 mb-2">Members ({members.length})</h4>
            {loading ? (
              <p className="text-sm text-gray-500">Loading members…</p>
            ) : members.length === 0 ? (
              <p className="text-sm text-gray-500">No members.</p>
            ) : (
              <div className="space-y-1 max-h-56 overflow-y-auto">
                {members.map((m) => (
                  <div key={m.id} className="flex items-center justify-between text-sm py-1">
                    <span className="text-gray-800">{m.fullName}</span>
                    <span className="text-xs text-gray-500">{m.email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const GroupManagementPage: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()

  const [rows, setRows] = useState<AdminGroupRow[]>([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<number | null>(null)
  const [viewGroup, setViewGroup] = useState<GroupDetail | null>(null)
  const [viewMembers, setViewMembers] = useState<GroupMember[]>([])
  const [viewLoading, setViewLoading] = useState(false)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!isAuthenticated) return navigate('/login')
    if (user && user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') navigate('/feed')
  }, [user, isAuthenticated, navigate])

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => { setDebounced(search); setPage(0) }, 300)
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current) }
  }, [search])

  const fetch = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      const data = await adminService.getAdminGroups(debounced, page, 20)
      setRows(data.content)
      setTotalPages(data.totalPages)
      setTotalElements(data.totalElements)
    } catch (e) {
      console.error('fetch groups', e)
    } finally {
      setLoading(false)
    }
  }, [debounced, page, user])

  useEffect(() => { fetch() }, [fetch])

  const handleDelete = async (id: number) => {
    if (!window.confirm('Force-delete this group? Members are removed. This cannot be undone.')) return
    setBusy(id)
    try {
      await adminService.deleteAdminGroup(id)
      fetch()
    } catch (e) {
      console.error('delete group', e)
      alert('Failed to delete group.')
    } finally {
      setBusy(null)
    }
  }

  const handleView = async (g: AdminGroupRow) => {
    setViewGroup(g)
    setViewMembers([])
    setViewLoading(true)
    try {
      const members = await adminService.getAdminGroupMembers(g.id)
      setViewMembers(members)
    } catch (e) {
      console.error('view group members', e)
    } finally {
      setViewLoading(false)
    }
  }

  const exportColumns: ExportColumn<AdminGroupRow>[] = [
    { header: 'ID', key: 'id' },
    { header: 'Name', key: 'name' },
    { header: 'Privacy', key: 'privacy' },
    { header: 'Category', key: 'category' },
    { header: 'Members', key: 'memberCount' },
    { header: 'Posts', key: 'postCount' },
    { header: 'Admin', key: 'admin', format: (v) => (v ? `${(v as any).firstName} ${(v as any).lastName}` : '—') },
    { header: 'Created', key: 'createdAt', format: (v) => new Date(v as string).toLocaleDateString() },
  ]

  return (
    <GovLayout>
      <div className="space-y-4">
        <GovPageTitle title="Group Management" subtitle={`${totalElements.toLocaleString()} group${totalElements === 1 ? '' : 's'}`} icon={Users} />

        <GovCard>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search groups by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <GovButton variant="secondary" size="sm" onClick={() => exportToCSV('groups', exportColumns, rows)} disabled={!rows.length}>
                <Download className="w-4 h-4 inline mr-1" /> CSV
              </GovButton>
              <GovButton variant="secondary" size="sm" onClick={() => exportToPDF('groups', 'Groups Report', exportColumns, rows)} disabled={!rows.length}>
                <FileText className="w-4 h-4 inline mr-1" /> PDF
              </GovButton>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-gray-500 p-8 text-center">Loading…</p>
          ) : rows.length === 0 ? (
            <GovEmptyState icon="👥" title="No Groups Found" description={search ? 'Try a different search.' : 'No groups have been created yet.'} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Name</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Privacy</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Category</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Members</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Posts</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Created</th>
                    <th className="text-right p-4 text-sm font-semibold text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((g) => (
                    <tr key={g.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="p-4 font-medium text-gray-900">{g.name}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${g.privacy === 'PUBLIC' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{g.privacy}</span>
                      </td>
                      <td className="p-4 text-sm text-gray-600">{g.category || '—'}</td>
                      <td className="p-4 text-sm text-gray-700">{g.memberCount}</td>
                      <td className="p-4 text-sm text-gray-700">{g.postCount}</td>
                      <td className="p-4 text-sm text-gray-600">{new Date(g.createdAt).toLocaleDateString()}</td>
                      <td className="p-4 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => handleView(g)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <Eye className="w-3.5 h-3.5" /> View
                          </button>
                          <button
                            onClick={() => handleDelete(g.id)}
                            disabled={busy === g.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> {busy === g.id ? '…' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-100">
                <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                  <ChevronLeft className="w-4 h-4" /> Prev
                </GovButton>
                <span className="text-sm text-gray-600">Page {page + 1} of {Math.max(1, totalPages)} · {totalElements} total</span>
                <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}>
                  Next <ChevronRight className="w-4 h-4" />
                </GovButton>
              </div>
            </div>
          )}
        </GovCard>
      </div>
      <GroupViewModal group={viewGroup} members={viewMembers} loading={viewLoading} onClose={() => setViewGroup(null)} />
    </GovLayout>
  )
}

export default GroupManagementPage